<template>
  <Modal
    :isOpen="isOpen"
    @update:isOpen="$emit('update:isOpen', $event)"
    modalId="add-container-modal"
    title="Add Container"
    subtitle="Fill out container details, then save."
    maxWidth="5xl"
    @close="onCancel"
  >
    <template #content>
      <div class="add-container-modal-body">
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Container Number</label>
            <input
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              :placeholder="isExportOrder ? 'Optional - leave blank if unknown' : 'ABCD1234567'"
              v-model="model.containerId"
              @blur="onContainerIdBlur"
            />
            <p
              v-if="isExportOrder"
              class="tw-mt-1.5 tw-text-xs tw-text-slate-500"
            >
              Export orders: leave blank to add a container before the number is known
            </p>
            <div v-if="shouldShowContainerNumberError" class="tw-mt-2 tw-text-sm tw-text-rose-600">Number not valid</div>
          </div>

          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Container Size</label>
            <div class="tw-mt-1">
              <FilterableDropdown
                v-model="model.containerSize"
                :options="['20', '24', '38', '40', '45', '53']"
                class="tw-w-full"
                :clearable="false"
                :components="{ OpenIndicator: null, Deselect: null }"
              ></FilterableDropdown>
            </div>
          </div>

          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Container Type</label>
            <div class="tw-mt-1">
              <FilterableDropdown
                v-model="model.containerType"
                :clearable="false"
                :options="['DS', 'DH', 'FS', 'FH', 'OS', 'OH', 'RS', 'RH']"
                class="tw-w-full"
                :components="{ OpenIndicator: null, Deselect: null }"
              ></FilterableDropdown>
            </div>
          </div>

          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Seal Number</label>
            <input
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              placeholder="Seal #"
              v-model="model.sealNumber"
            />
          </div>

          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Cargo Pieces</label>
            <input
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              placeholder="Pieces"
              v-model="model.cargoPieces"
            />
          </div>

          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Weight</label>
            <input
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              placeholder="Weight"
              v-model="model.weight"
            />
          </div>

          <div class="md:tw-col-span-2">
            <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Cargo Description</label>
            <input
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              placeholder="Description"
              v-model="model.cargoDescription"
            />
          </div>
        </div>

        <div class="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
          <Button
            @click="onSave"
            :disabled="!canSave || isSaving"
            :enableLoading="isSaving"
            class="tw-h-9"
            icon="check"
            title="Save container"
            aria-label="Save container"
          >Save</Button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';

import Modal from '@/components/core/Modal/Modal.vue';
import FilterableDropdown from '@/components/core/FilterableDropdown.vue';
import Button from '@/components/core/Button.vue';
import isContainer from '@/utils/container_validator';

export type AddContainerModel = {
  containerId?: string;
  containerSize?: string;
  containerType?: string;
  cargoPieces?: string;
  weight?: string;
  cargoDescription?: string;
  sealNumber?: string;
};

export default defineComponent({
  components: {
    Modal,
    FilterableDropdown,
    Button,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    isExportOrder: {
      type: Boolean,
      default: false,
    },
    model: {
      type: Object as PropType<AddContainerModel>,
      required: true,
    },
  },
  emits: ['update:isOpen', 'cancel', 'save'],
  data() {
    return {
      containerIdTouched: false,
      saveAttempted: false,
    };
  },
  watch: {
    isOpen(val: boolean) {
      if (val) {
        (this as any).containerIdTouched = false;
        (this as any).saveAttempted = false;
      }
    },
  },
  computed: {
    hasContainerSize(): boolean {
      const vm = (this as any);
      return !!String(vm.model?.containerSize || '').trim();
    },
    hasContainerType(): boolean {
      const vm = (this as any);
      return !!String(vm.model?.containerType || '').trim();
    },
    isContainerNumberValid(): boolean {
      const vm = (this as any);
      const id = (vm.model?.containerId || '').trim();
      if (!id) {
        return vm.isExportOrder;
      }
      if (id.length < 11) return false;
      return isContainer(id);
    },
    canSave(): boolean {
      const vm = (this as any);
      return vm.isContainerNumberValid && vm.hasContainerSize && vm.hasContainerType;
    },
    shouldShowContainerNumberError(): boolean {
      const vm = (this as any);
      const id = (vm.model?.containerId || '').trim();
      if (!vm.containerIdTouched && !vm.saveAttempted) return false;
      if (!id) {
        return vm.saveAttempted && !vm.isExportOrder;
      }
      return !vm.isContainerNumberValid;
    },
  },
  methods: {
    onContainerIdBlur() {
      (this as any).containerIdTouched = true;
    },
    onCancel() {
      (this as any).$emit('cancel');
    },
    onSave() {
      const vm = (this as any);
      vm.saveAttempted = true;
      if (!vm.canSave || vm.isSaving) return;
      vm.$emit('save');
    },
  },
});
</script>

<style lang="scss" scoped>
.add-container-modal-body {
  :deep(input) {
    background-color: #ffffff !important;
  }

  :deep(input:-webkit-autofill),
  :deep(input:-webkit-autofill:hover),
  :deep(input:-webkit-autofill:focus),
  :deep(input:-webkit-autofill:active) {
    -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
    box-shadow: 0 0 0 1000px #ffffff inset !important;
    -webkit-text-fill-color: #0f172a !important;
    caret-color: #0f172a;
  }

  :deep(.v-select-tw .vs__dropdown-toggle) {
    background: #ffffff;
  }

  :deep(.v-select-tw:hover .vs__dropdown-toggle) {
    background: #ffffff;
  }

  :deep(.v-select-tw:focus-within .vs__dropdown-toggle) {
    background: #ffffff;
  }
}
</style>
